import moment from 'moment';
import { callApi, getFullNameForSaveEntertainment, isSplash } from '../../utils';
import { onSaveDining } from './dinings';
import {
  REQUEST_SAVE_ADDON,
  RECEIVE_SAVE_ADDON,
  SET_SAVE_ADDON_FAILURE,
  REQUEST_SAVE_ENTERTAINMENT,
  RECEIVE_SAVE_ENTERTAINMENT,
  SET_SAVE_ENTERTAINMENT_FAILURE,
  REQUEST_DELETE_ENTERTAINMENT,
  RECEIVE_DELETE_ENTERTAINMENT,
  SET_DELETE_ENTERTAINMENT_FAILURE,
  CLEAR_SAVE_ADDON_ERROR_MESSAGE,
} from '../constants/changeAddon';
import { bookMealTimesGTM, bookEntertainmentGTM, editBookingGTM } from '../../googleTagManager';

const requestSaveAddon = () => ({
  type: REQUEST_SAVE_ADDON,
});

const receiveSaveAddon = invoice => ({
  type: RECEIVE_SAVE_ADDON,
  invoice,
});

const setSaveAddonFailure = error => ({
  type: SET_SAVE_ADDON_FAILURE,
  error,
});

export const clearSaveAddonErrorMessage = () => ({
  type: CLEAR_SAVE_ADDON_ERROR_MESSAGE,
});

const reportMealsToGTM = (isEditBooking, action, result, selectedAddonType, selectedAddonStart, selectedAddonEnd, selectedAddonEventId, error = {}) => {
  if (isEditBooking && action !== 'cancel') {
    editBookingGTM('meals', result, selectedAddonType, '', selectedAddonStart, selectedAddonEnd, selectedAddonEventId);
  } else {
    bookMealTimesGTM(selectedAddonType, action, 'success', selectedAddonStart, selectedAddonEnd, selectedAddonEventId, error);
  }
};

export const saveAddon = (resId, addons, action, isEditBooking = false) => async (dispatch, getState) => {
  dispatch(requestSaveAddon());

  const { config } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: bookingSubscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
  } = config;

  const selectedAddonByQuantity = addons.filter(({ quantity }) => quantity > 0);
  const selectedAddonToSendGTM = selectedAddonByQuantity.length > 0
    ? selectedAddonByQuantity
    : addons.filter(({ previouslyBooked }) => previouslyBooked);
  const [{
    type: selectedAddonType,
    startTime: selectedAddonStart,
    endTime: selectedAddonEnd,
    code: selectedAddonEventId,
  }] = selectedAddonToSendGTM;

  try {
    const formattedAddons = addons.map(({ code, quantity }) => ({
      code,
      quantity,
    }));
    const { data = {} } = await callApi(
      {
        method: 'put',
        url: `/common/account/booking/${resId}/addon`,
        data: {
          units: [
            {
              unitSeqNo: 0,
              addons: formattedAddons,
            },
          ],
        },
      },
      bookingPrefix,
      bookingSubscriptionKey,
      timeout,
    );
    reportMealsToGTM(isEditBooking, action, 'success', selectedAddonType, selectedAddonStart, selectedAddonEnd, selectedAddonEventId);
    dispatch(receiveSaveAddon(data.Data));
    dispatch(onSaveDining());
  } catch (error) {
    reportMealsToGTM(isEditBooking, action, 'error', selectedAddonType, selectedAddonStart, selectedAddonEnd, selectedAddonEventId, error);
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setSaveAddonFailure(error));
  }
};

const requestSaveEntertainment = () => ({
  type: REQUEST_SAVE_ENTERTAINMENT,
});

const receiveSaveEntertainment = invoice => ({
  type: RECEIVE_SAVE_ENTERTAINMENT,
  invoice,
});

const setSaveEntertainmentFailure = error => ({
  type: SET_SAVE_ENTERTAINMENT_FAILURE,
  error,
});

const reportSaveEntertainmentGTM = (entertainmentsToSave = [], type = 'entertainment', result = 'error', error = {}) => {
  if (!entertainmentsToSave || entertainmentsToSave.length === 0) return;
  entertainmentsToSave.forEach(({ selectedFullDate, timeSlotId, duration, name, needToReportToGTM, reportType = 'book' }) => {
    if (needToReportToGTM) {
      const eventDate = moment(selectedFullDate).format('YYYY-MM-DD');
      const startTime = moment(selectedFullDate).format('HH:mm');
      const endTime = moment(selectedFullDate).add(duration, 'minutes').format('HH:mm');
      if (reportType === 'edit') {
        editBookingGTM(type, result, name, eventDate, startTime, endTime, timeSlotId, error);
      } else {
        bookEntertainmentGTM(
          type,
          'book',
          result,
          name,
          eventDate,
          startTime,
          endTime,
          timeSlotId,
          error,
        );
      }
    }
  });
};

export const saveEntertainment = (entertainmentsToSave, type = 'entertainment') => async (dispatch, getState) => {
  dispatch(requestSaveEntertainment());
  const { config } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: bookingSubscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
  } = config;
  const entertainmentPromises = entertainmentsToSave.map(({ resId, siteCode, activityId, date, timeSlotId, quantity = 1 }) => callApi(
    {
      method: 'post',
      url: '/common/activity/reservation',
      data: {
        ResId: resId,
        ParkCode: siteCode.toUpperCase(),
        ActivityId: activityId,
        ActivityDate: date,
        TimeslotId: timeSlotId,
        Quantity: quantity,
        Customer: {
          Name: getFullNameForSaveEntertainment(),
        },
      },
    },
    bookingPrefix,
    bookingSubscriptionKey,
    timeout,
  ));
  try {
    const results = await Promise.all(entertainmentPromises);
    reportSaveEntertainmentGTM(entertainmentsToSave, type, 'success');
    const invoices = results.map(({ data = {} }) => data.Data);
    dispatch(receiveSaveEntertainment(invoices.flat()));
  } catch (error) {
    reportSaveEntertainmentGTM(entertainmentsToSave, type, 'error', error);
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setSaveEntertainmentFailure(error));
  }
};

const requestDeleteEntertainment = () => ({
  type: REQUEST_DELETE_ENTERTAINMENT,
});

const receiveDeleteEntertainment = invoice => ({
  type: RECEIVE_DELETE_ENTERTAINMENT,
  invoice,
});

const setDeleteEntertainmentFailure = error => ({
  type: SET_DELETE_ENTERTAINMENT_FAILURE,
  error,
});

const reportDeleteEntertainmentGTM = (result = 'error', eventDate, startTime, endTime, timeSlotId, name, needToReportToGTM, error = {}) => {
  if (!eventDate || !startTime || !endTime || !timeSlotId || !name || !needToReportToGTM) {
    return;
  }
  const eventType = isSplash(name) ? 'swimming' : 'entertainment';
  bookEntertainmentGTM(
    eventType,
    'cancel',
    result,
    name,
    eventDate,
    startTime,
    endTime,
    timeSlotId,
    error,
  );
};

export const deleteEntertainment = (
  resId,
  siteCode,
  timeSlotId,
  eventDate,
  startTime,
  endTime,
  name,
  needToReportToGTM = false,
) => async (dispatch, getState) => {
  if (!resId || !siteCode || !timeSlotId) return;
  dispatch(requestDeleteEntertainment());
  const { config } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: bookingSubscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
  } = config;
  try {
    await callApi(
      {
        method: 'delete',
        url: `/common/activity/reservation?ResId=${resId}&ParkCode=${siteCode.toUpperCase()}&TimeslotId=${timeSlotId}`,
      },
      bookingPrefix,
      bookingSubscriptionKey,
      timeout,
    );
    reportDeleteEntertainmentGTM('success', eventDate, startTime, endTime, timeSlotId, name, needToReportToGTM);
    dispatch(receiveDeleteEntertainment());
  } catch (error) {
    reportDeleteEntertainmentGTM('error', eventDate, startTime, endTime, name, needToReportToGTM, error);
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setDeleteEntertainmentFailure(error));
  }
};
